/**
 * Client project links.
 *
 * Every project has one permanent, shareable URL — portalproductions.me/project/
 * <slug> — derived from the client and project name. Everyone on the client
 * team uses the same link; the slug never changes once issued.
 *
 * A show has the same shape of link, one level up: /show/<slug>. Both
 * prefixes share the slugify/uniqueSlug helpers since a slug is just a slug.
 *
 * ONE FILM, ONE LINK — 26 Sep 2026.
 *
 * `…/project/<slug>/v/<deliverableId>?access=…` opens the project page with
 * that film already in the player. Until this existed, NOTHING below the
 * project was addressable: a client could not forward the cut to a colleague,
 * could not bookmark anything, and lost their place on refresh — every link
 * they could copy dropped the recipient on the front page.
 *
 * That matters more than it sounds. For a production company, a client
 * sending the work round their own building IS the marketing; a page that
 * cannot be linked to cannot be forwarded, and does none of that work.
 *
 * The access token stays in the query string, so a forwarded link carries the
 * same permission the sender had — no second gate, and no new way in.
 * `slugFromPath` keeps working untouched: it already split on '/' and took
 * the first segment.
 */

export const CLIENT_LINK_PREFIX = '/project/'
export const SHOW_LINK_PREFIX = '/show/'

/** Between slug and deliverable id. Short on purpose — it is in every shared link. */
const ITEM_MARKER = 'v'

/** "Suited & Booted — Podcast" → "suited-and-booted-podcast". */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/&/g, ' and ')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** A slug for this client and project (or client and show) that no existing one is using. */
export function uniqueSlug(client: string, name: string, taken: string[]): string {
  const base = slugify(`${client} ${name}`) || 'project'
  if (!taken.includes(base)) return base

  let counter = 2
  while (taken.includes(`${base}-${counter}`)) counter += 1
  return `${base}-${counter}`
}

/** The path this prototype navigates to, e.g. "/project/atm-tv". */
export function clientPath(slug: string): string {
  return `${CLIENT_LINK_PREFIX}${slug}`
}

/**
 * The link Portal staff send to a client — always on whatever domain the app
 * is currently served from, so connecting a custom domain needs no code change.
 *
 * The access token is part of the link, not an optional extra: without one
 * there is no link to give out, so this returns '' rather than a slug-only URL
 * that would fail at join_project.
 */
export function clientLink(slug: string, accessToken: string): string {
  if (!slug || !accessToken) return ''
  const origin =
    typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''
  return `${origin}${clientPath(slug)}?access=${encodeURIComponent(accessToken)}`
}

/** The slug in a path, or '' when the path is not a client project link. */
export function slugFromPath(path: string): string {
  if (!path.startsWith(CLIENT_LINK_PREFIX)) return ''
  return path.slice(CLIENT_LINK_PREFIX.length).split('/')[0] ?? ''
}

/** The show path this prototype navigates to, e.g. "/show/bloomberg-economy-of-scale". */
export function showClientPath(slug: string): string {
  return `${SHOW_LINK_PREFIX}${slug}`
}

/** Mirrors clientLink() exactly, one level up. */
export function showClientLink(slug: string, accessToken: string): string {
  if (!slug || !accessToken) return ''
  const origin =
    typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''
  return `${origin}${showClientPath(slug)}?access=${encodeURIComponent(accessToken)}`
}

/** The slug in a path, or '' when the path is not a client show link. */
export function showSlugFromPath(path: string): string {
  if (!path.startsWith(SHOW_LINK_PREFIX)) return ''
  return path.slice(SHOW_LINK_PREFIX.length).split('/')[0] ?? ''
}

/**
 * The deliverable named by a client link, or '' when it names none.
 *
 * Works under either prefix, so a show link can point at one film too.
 * Anything that is not exactly `<slug>/v/<id>` returns '' rather than
 * guessing — an unrecognised tail must fall back to the whole project, never
 * to an empty screen.
 */
export function deliverableFromPath(path: string): string {
  const prefix = path.startsWith(CLIENT_LINK_PREFIX)
    ? CLIENT_LINK_PREFIX
    : path.startsWith(SHOW_LINK_PREFIX)
      ? SHOW_LINK_PREFIX
      : ''
  if (!prefix) return ''

  const parts = path.slice(prefix.length).split('/')
  if (parts[1] !== ITEM_MARKER) return ''

  try {
    return decodeURIComponent(parts[2] ?? '')
  } catch {
    return ''
  }
}

/** `/project/<slug>/v/<id>` — the path for one film inside a project. */
export function deliverablePath(slug: string, deliverableId: string): string {
  if (!slug) return ''
  if (!deliverableId) return clientPath(slug)
  return `${clientPath(slug)}/${ITEM_MARKER}/${encodeURIComponent(deliverableId)}`
}

/**
 * The full link to one film, for a client to copy and send.
 *
 * Built from the CURRENT address rather than from stored values, so it
 * carries whatever access token the sender arrived with and works on any
 * domain the app is served from.
 */
export function shareableItemLink(deliverableId: string): string {
  if (typeof window === 'undefined') return ''

  const { origin, pathname, search } = window.location
  const slug = slugFromPath(pathname) || showSlugFromPath(pathname)
  if (!slug) return ''

  return `${origin}${deliverablePath(slug, deliverableId)}${search}`
}
